"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import useAuth from "@/hooks/auth/useAuth";
import { Loader2, Edit3 } from "lucide-react";
import { toast } from "sonner";

const profileFormSchema = z.object({
  first_name: z.string().min(1, "First name is required"),
  last_name: z.string().min(1, "Last name is required"),
  phone: z.string().optional(),
});

type ProfileFormValues = z.infer<typeof profileFormSchema>;

export function UserProfileDialog() {
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: {
      first_name: user?.user_metadata.first_name || "",
      last_name: user?.user_metadata.last_name || "",
      phone: user?.phone || "",
    },
  });

  // Prevent dialog from closing during operations
  const handleOpenChange = (newOpen: boolean) => {
    // Don't allow closing during loading states
    if (loading) {
      return;
    }
    setOpen(newOpen);
  };

  const onSubmit = async (values: ProfileFormValues) => {
    if (!user) {
      toast.error("User authentication required. Please refresh the page and try again.");
      return;
    }

    try {
      setLoading(true);

      await updateUser(values.first_name, values.last_name, values.phone);

      toast.success("Profile updated successfully!");

      // Close the dialog - no need to refresh user data since dialog is closing
      setOpen(false);
    } catch (error: any) {
      // Better error logging and handling
      const errorMessage = error?.message || error?.details || error?.hint || "Unknown error occurred";
      const errorCode = error?.code || "UNKNOWN";

      console.error("Error updating profile:", {
        message: errorMessage,
        code: errorCode,
        details: error?.details,
        hint: error?.hint,
        fullError: error,
      });

      // Don't close dialog on error - let user try again
      let userFriendlyMessage = "Failed to update profile. Please try again.";

      if (errorCode === "42501") {
        userFriendlyMessage = "Permission denied. Please contact support.";
      } else if (errorCode === "23505") {
        userFriendlyMessage = "A profile already exists for this user.";
      } else if (errorCode === "PGRST301") {
        userFriendlyMessage = "Database error. Please try again in a moment.";
      } else if (errorMessage && errorMessage !== "Unknown error occurred") {
        userFriendlyMessage = errorMessage;
      }

      toast.error(userFriendlyMessage);

      // Don't close the dialog so user can try again
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          className="w-full h-10 justify-start px-6 text-slate-600 hover:text-slate-900 hover:bg-slate-100"
        >
          <Edit3 className="h-4 w-4 mr-3" />
          <span className="text-sm">Edit Profile</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Edit Profile</DialogTitle>
          <DialogDescription>
            Update your profile information. This will be displayed throughout the application.
          </DialogDescription>
        </DialogHeader>

        {!user ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <p className="text-sm text-slate-500 mb-2">User authentication required</p>
              <Button variant="outline" onClick={() => window.location.reload()}>
                Refresh Page
              </Button>
            </div>
          </div>
        ) : (
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit, (errors) => {
                console.log("Form validation errors:", errors);
                // Don't close dialog on validation errors
                toast.error("Please fix the form errors before submitting.");
              })}
              className="space-y-4"
            >
              <FormField
                control={form.control}
                name="first_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Sarah" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="last_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Judd" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={loading}>
                  Cancel
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Save Changes
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
